import { createFileRoute, Link } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service | Nkadimeng Danny Legal & Compliance Services" },
      {
        name: "description",
        content:
          "The terms governing your use of our website and engagement of our paralegal, legal-support and compliance services.",
      },
      { property: "og:title", content: "Terms of Service" },
      {
        property: "og:description",
        content: "Terms governing use of our website and services.",
      },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Last updated: 15 June 2026. By using this website or engaging our
            services you agree to these terms.
          </p>
        </div>
      </section>

      <section className="container-narrow py-12 md:py-16 space-y-8 text-sm md:text-base leading-relaxed">
        <Block title="1. Scope of services">
          <p>
            Nkadimeng Danny Legal &amp; Compliance Services provides paralegal,
            legal-support and compliance services. We are{" "}
            <strong>not an admitted firm of attorneys</strong>, we do not
            appear in court, we do not give legal advice, and we do not
            operate a trust account. Where your matter requires an admitted
            attorney we will refer you. See our{" "}
            <Link to="/disclaimer" className="text-navy underline hover:text-gold">
              full disclaimer
            </Link>
            .
          </p>
        </Block>

        <Block title="2. Bookings and fees">
          <p>
            Fees are quoted in South African Rand and confirmed before work
            begins. Consultation fees are payable on booking. Additional
            work is invoiced according to the scope agreed in writing.
          </p>
        </Block>

        <Block title="3. Cancellations and refunds">
          <p>
            You may reschedule or cancel a consultation up to 24 hours
            before the appointment for a full credit or refund. Cancellations
            within 24 hours or no-shows are non-refundable.
          </p>
        </Block>

        <Block title="4. Client responsibilities">
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide accurate, complete information about your matter.</li>
            <li>Disclose all documents that may be relevant.</li>
            <li>Respond timeously to requests for input or signatures.</li>
          </ul>
        </Block>

        <Block title="5. Confidentiality">
          <p>
            Information you share is treated as confidential and processed in
            line with our{" "}
            <Link to="/privacy" className="text-navy underline hover:text-gold">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/popia" className="text-navy underline hover:text-gold">
              POPIA Policy
            </Link>
            .
          </p>
        </Block>

        <Block title="6. Intellectual property">
          <p>
            All website content, templates and resources remain the property
            of Nkadimeng Danny unless otherwise stated. You may not
            reproduce, resell, or redistribute them without written
            permission.
          </p>
        </Block>

        <Block title="7. Limitation of liability">
          <p>
            To the maximum extent permitted by law, our total liability for
            any claim arising from the services is limited to the fees you
            paid for the specific matter in question. We are not liable for
            indirect or consequential loss.
          </p>
        </Block>

        <Block title="8. Governing law">
          <p>
            These terms are governed by the laws of the Republic of South
            Africa. Any dispute is subject to the jurisdiction of the South
            African courts.
          </p>
        </Block>

        <Block title="9. Complaints">
          <p>
            If you are unhappy with any aspect of our service, please follow
            our{" "}
            <Link to="/complaints" className="text-navy underline hover:text-gold">
              complaints procedure
            </Link>
            . You can also reach us directly at{" "}
            <a href={`mailto:${site.email}`} className="text-navy underline hover:text-gold">
              {site.email}
            </a>
            .
          </p>
        </Block>
      </section>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-navy">{title}</h2>
      <div className="mt-3 text-muted-foreground">{children}</div>
    </div>
  );
}
